import { makeStyles, createTheme } from "@material-ui/core/styles";
import _ from "lodash";
import React from "react";

// style constants for consistent styling
export const grayDark = "#bcbcbc"; //"#454545";
export const white = "fffff";
export const textColor = "#424242";
export const textSecondary = "rgba(0, 0, 0, 0.54)";
export const primaryColorLight = "#E71650";
export const secondaryColor = "#FF9472";
export const maxContentHeight = "775px";
export const maxDisplayHeight = "800px";
export const bodyTextSize = "1em";
export const headerTextSize = "1.2em";
export const descriptionTextSize = "0.9em";
export const tooltipTextSize = "0.8em";
export const grayedoutGray = "#c51344";
export const buttonTextSize = "0.85em";

export const colors = {
  Car: "#7f0133",
  Tree: "#ff936e",
  Star: "#4054db",
  noGroup: "#AAA"
};
export const hardColors = [colors.Car, colors.Tree, colors.Star, colors.noGroup];
export const popoverColor = "white"; //"#efefef";
export const graphicsDarkRed = "#c51344";
export const borderRadiusValue = "12px";

const breakpoints = [700, 998];

// resize handler
// uses constant breakpoint array
// returns array containing ref to pass to container div and breakpoint check result
export const useNeedResize = () => {
  const [needResize, setNeedResize] = React.useState([false, false, false]);
  const containerRef = React.useRef(null);

  React.useEffect(() => {
    if (containerRef.current) {
      const containerSize = containerRef.current.clientWidth;
      setNeedResize([
        containerSize < breakpoints[0],
        containerSize >= breakpoints[0] && containerSize < breakpoints[1],
        containerSize >= breakpoints[1]
      ]);
      const resizeObserver = new ResizeObserver((elements) => {
        const element = elements[0];
        const containerSize = element.target.clientWidth;
        setNeedResize([
          containerSize < breakpoints[0],
          containerSize >= breakpoints[0] && containerSize < breakpoints[1],
          containerSize >= breakpoints[1]
        ]);
      });

      resizeObserver.observe(containerRef.current);
      return () => resizeObserver.disconnect();
    }
  }, [containerRef, setNeedResize]);

  return [needResize, containerRef];
};

// text handler
export const createShort = (short, shortLength) => {
  return _.map(short.slice(0, shortLength), (value, index) => {
    return index === shortLength - 1 ? `${value}...` : `${value} `;
  });
};

//  mui theme to override mui component styling
export const theme = createTheme({
  palette: {
    type: "light",

    primary: {
      main: "#C51344"
    }
  },
  overrides: {
    MuiBackdrop: {
      root: {
        backgroundColor: "transparent",
        boxShadow: "rgb(213, 213, 213) 6px 6px 12px, rgb(255, 255, 255) -6px -6px 12px"
      }
    },

    MuiButton: {
      outlined: {
        color: graphicsDarkRed,
        borderColor: graphicsDarkRed,
        fontSize: buttonTextSize,
        borderRadius: "6px",
        "&:hover": {
          border: " 1px solid #C51344",
          backgroundColor: "rgba(197, 19, 68, 0.04)"
        }
      },

      contained: {
        color: "white",
        borderRadius: "6px"
      }
    },

    MuiTooltip: {
      arrow: {
        // color: graphicsDarkRed,
        color: "white",
        "&:before": {
          // border: "1px solid " + graphicsDarkRed
        }
      },

      tooltip: {
        height: "auto",
        // backgroundColor: graphicsDarkRed,
        // color: "white",
        color: textColor,
        backgroundColor: popoverColor,
        padding: "10px",
        lineHeight: "140%",
        fontSize: tooltipTextSize,
        fontWeight: "normal",
        borderRadius: borderRadiusValue,
        boxShadow: "rgb(213, 213, 213) 6px 6px 12px, rgb(255, 255, 255) -6px -6px 12px",
        marginTop: "7px"
      }
    }
  }
});

// styles for stepContainers
export const useStepContainerStyles = makeStyles(
  {
    animationContainer: {
      height: "100%"
    },

    textContainerGrid: {
      display: "flex",
      flexWrap: "wrap",
      overflow: (props) => (props.scrolling ? "hidden" : "auto"),
      minWidth: "80%",
      width: "100%",
      height: "98%",
      alignSelf: "center",
      justifyContent: "center",
      flexGrow: "1",
      gap: "1%"
    }
  },
  { name: "StepContainerStyles" }
);

// styles for text cards in step 1 and 2
export const useCardStyles = makeStyles(
  {
    root: {
      minWidth: 250,
      maxWidth: 450,
      maxHeight: (props) => (props.page === 1 ? 270 : 230),
      width: "40%",
      margin: 10,
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      flexShrink: 1,
      color: `${textColor} !important`,
      padding: 16,
      boxShadow: "0 1.5px 12px 2px rgb(0 0 0 / 6%)",
      borderRadius: borderRadiusValue,
      backgroundColor: "#ffffff"
    },

    checkBoxContainer: {
      width: "100%",
      top: "auto",
      bottom: "auto"
    },

    closeButtonContainer: {
      width: "100%"
    },

    fullHeight: {
      overflow: "hidden",
      display: "flex",
      flexWrap: "wrap",
      flexDirection: "column",
      width: "90%",
      justifyContent: "center",
      height: "88%",
      paddingBottom: 0
    },

    flexNoGrowNoShrink: {
      flexGrow: 0,
      flexShrink: 1
    },

    headerStyling: {
      // fontSize: (props) => (props.sizeBreak ? bodyTextSize : headerTextSize),
      fontSize: "1.2rem"
    },

    dialogStyling: {
      minWidth: 250,
      maxWidth: 500,
      borderRadius: borderRadiusValue + " !important",
      boxShadow: "rgb(213, 213, 213) 6px 6px 12px, rgb(255, 255, 255) -6px -6px 12px !important"
    },

    dialogHeaderStyling: {
      fontSize: headerTextSize,
      marginBottom: "0px",
      paddingBottom: "5px"
    },

    bodyStyling: {
      fontSize: bodyTextSize,
      marginTop: "3px",
      borderRadius: borderRadiusValue,
      flexShrink: 1
    },

    dialogBodyStyling: {
      fontSize: bodyTextSize,
      marginTop: "0px",
      paddingTop: "0px !important"
    },

    dialogContentContainer: {
      paddingTop: 0
    },

    dialogActionStyling: {
      padding: 0,
      paddingBottom: "15px"
    },

    dialogButtonStyling: {
      color: graphicsDarkRed,
      borderColor: graphicsDarkRed,
      "&:hover": {
        border: " 1px solid #C51344",
        backgroundColor: "rgba(197, 19, 68, 0.04)"
      }
    },

    linkStyling: {
      marginTop: "4px",
      cursor: "pointer",
      color: graphicsDarkRed,
      fontSize: bodyTextSize
    },

    buttonStyling: {
      fontSize: buttonTextSize,
      width: "100%"
    },

    dialogBackdrop: {
      borderRadius: borderRadiusValue + " !important"
    }
  },
  { name: "TextCardStyles" }
);

// styles for taxonomy picking in step 3
export const useStepThreeStyles = makeStyles((theme) => ({
  header: {
    textAlign: "left",
    marginBottom: "2px",
    fontSize: (props) => (props.sizeBreak ? bodyTextSize : headerTextSize)
  },
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    height: "95%",
    overflow: "auto",
    marginBottom: "0px !important"
  },
  fullHeight: {
    overflow: "auto",
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    width: "90%",
    padding: "5px !important"
  },
  infoIconStyling: {
    marginLeft: "5px",
    marginTop: (props) => (props.sizeBreak ? "-4px" : "-2px"),
    color: graphicsDarkRed,
    fontSize: "x-large",
    cursor: "pointer",
    padding: "5px"
  },
  headerContainer: {
    flexShrink: "0",
    display: "flex",
    flexDirection: "row",
    overflow: "hidden"
  },
  popoverStyling: {
    width: "80%"
  },
  contentContainer: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    flexShrink: 1,
    flexWrap: "noWrap",
    margin: "10px",
    overflow: "auto"
  },
  paper: {
    boxShadow: "none"
  },
  toggleButtonContainer: {
    display: "flex",
    flexGrow: 1,
    flexShrink: 0,
    justifyContent: "center"
  }
}));
